import { User } from 'models/User';
import { DataRoomNode } from './DataRoomNode';
import {
  DataRoomBuySideGroup,
  DataRoomMember,
  DataRoomMemberRole,
} from './DataRoomMember';
import { DataRoom } from './DataRoom';
import { Q_AND_A_QUESTION_STATUS } from '../components/QAndARoleFilter/data';
import { FAQAccessInfo } from './FAQ';
import { ObjectValues } from 'types';
import { UploadedFile } from 'models/File';

export const Q_AND_A_SIDES = {
  SELL: 'sell',
  BUY: 'buy',
} as const;

export const Q_AND_A_ROLES = {
  // Sell
  OWNER: 'Owner',
  ADMIN_SELL: 'Admin Sell',
  COORDINATOR: 'Coordinator',
  EXPERT: 'Expert',
  ANSWER_APPROVER: 'Answer Approver',

  // Buy
  ADMIN_BUY: 'Admin Buy',
  QUESTION_APPROVER: 'Question Approver',
  DRAFTER: 'Drafter',

  // Both
  NONE: 'None', // by default sell side members have None role
} as const;

export type QAndARoles = ObjectValues<typeof Q_AND_A_ROLES>;
export type QAndASides = ObjectValues<typeof Q_AND_A_SIDES>;

export type QuestionStatus = ObjectValues<typeof Q_AND_A_QUESTION_STATUS>;

export type Question = {
  _id: string;
  subject: string;
  otherSubject?: string;
  content: string;
  priority: QuestionPriority;
  user: User;
  createdAt: Date;
  submittedDate: Date;
  publishedDate: Date;
  dataRoomNodeId?: string;
  dataRoomId: string;
  dataRoomNode?: DataRoomNode;
  dataRoomAnswer?: Answer;
  status: QuestionStatus;
  dataRoomAnswerId?: string;
  assignedUserIds?: string[];
  assignedUsernames?: string[];
  rejectedBy?: QAndASides;
  rejectQuestionRespond?: string;
  userId: string;
  hasAccessDataRoomNode?: boolean;
  isPublishedToFAQ?: boolean;
  publishedDateToFAQ?: Date;
  openDrawerDefaultly?: boolean;
  isFromQA?: boolean;
  rejectedUser?: User;
  dataRoomBuySideGroup?: DataRoomBuySideGroup;

  files: QuestionFile[];

  //follow-up
  dataRoomParentQuestionId?: string;
  dataRoomParentQuestion?: Question;
  dataRoomLatestFollowupQuestion?: Question;
  dataRoomPreviousFollowupQuestion?: Question;
} & FAQAccessInfo;

export type Answer = {
  _id: string;
  content: string;
  rejectAnswerRespond: string;
  userId: string;
  user: Partial<User> | null;
  // data room member who answered the question
  dataRoomMemberId: string;
  dataRoomMember: Partial<DataRoomMember> | null;
  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;
  // related question
  dataRoomQuestionId: string;
  dataRoomQuestion: Partial<Question> | null;
  rejectedUser?: User;

  files: AnswerFile[];
};

export const QUESTION_PRIORITIES = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
} as const;

export type QuestionPriority = ObjectValues<typeof QUESTION_PRIORITIES>;

export type DataRoomQuestionAnswererMembers = {
  dataRoomQuestionAnswerersGroupByTeams?: Record<
    string,
    Pick<DataRoomMember, '_id' | 'fullName' | 'userId' | 'email'>[]
  >;
  allDataRoomQuestionAnswerers?: {
    teams?: string[];
    isLead: boolean;
    isDataRoomLoggedIn: boolean;
    _id: string;
    fullName: string;
    role: DataRoomMemberRole;
    email: string;
    userId: string;
    dataRoomId: string;
    isSetup: boolean;
    isAccepted: boolean;
    qaRole: QAndARoles;
    qaSide: QAndASides;
  }[];
};

export type QuestionFilterCount = {
  allCount: number;
  needsApprovalCount?: number;
  publishedCount?: number;
  inReviewCount?: number;
  awaitingAnswerCount?: number;
  answeredCount?: number;
  unanswered?: number;
};

export type QuestionManagingDrawerCommonProps = {
  question: Question;
  canFollowUp?: boolean;
  onClose?: () => void;
  onQuestionsUpdate?: () => void;
  updateQuestion: (question: Partial<Question>) => void;
};

export type QuestionFile = UploadedFile & {
  dataRoomQuestioId: string;
  dataRoomQuestion: Partial<Question> | null;
};

export type AnswerFile = UploadedFile & {
  dataRoomAnswerId: string;
  dataRoomAnswer: Partial<Answer> | null;
};
